import React from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'

import GoogleIcon from '../../../components/icon/GoogleIcon'
import { formatMoney, formatQuantity, toSafeNumber } from '../../../helpers/utils'
import { UI_TEXT_EM_DASH } from '../../../constants/uiText'
import { REFUND_APPROVAL_TEXT } from '../../constants/refundApprovalText'
import { getRefundLineStatusUI } from '../../constants/refundLineStatus'
import { getRefundLineCondition } from '../../helpers/refundApprovalUtils'
import { getPrimaryBarcode } from '../helpers/refundScanModalUtils'
import {
  UI_COLOR_BORDER_CARD_NEUTRAL,
  UI_COLOR_BORDER_DIVIDER_SOFT,
  UI_COLOR_BORDER_MEDIUM,
  UI_COLOR_DANGER_OUTLINE,
  UI_COLOR_DANGER_SURFACE,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_PRIMARY_PRESS,
  UI_COLOR_SHADOW_CARD,
  UI_COLOR_SURFACE_DISABLED_BTN,
  UI_COLOR_SURFACE_SUCCESS_ROW,
  UI_COLOR_TEXT_CARD_BODY,
  UI_COLOR_TEXT_CODE,
  UI_COLOR_TEXT_DISABLED,
  UI_COLOR_WHITE
} from '../../../constants/uiColors'

const DetailRow = ({ label, value, labelSuffix = ':' }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      alignItems: 'baseline',
      gap: 1.5,
      py: 0.55
    }}
  >
    <Typography
      component="span"
      sx={{
        minWidth: 0,
        fontSize: 13,
        fontWeight: 500,
        color: UI_COLOR_TEXT_CARD_BODY,
        lineHeight: 1.35,
        textTransform: 'uppercase',
        letterSpacing: 0
      }}
    >
      {labelSuffix ? `${label}${labelSuffix}` : label}
    </Typography>
    <Typography
      component="span"
      sx={{
        minWidth: 0,
        maxWidth: { xs: '48vw', sm: '56vw' },
        fontSize: 13,
        fontWeight: 800,
        color: UI_COLOR_TEXT_CARD_BODY,
        lineHeight: 1.35,
        textAlign: 'right',
        overflowWrap: 'anywhere'
      }}
    >
      {value}
    </Typography>
  </Box>
)

const SectionDivider = () => (
  <Box
    sx={{
      borderTop: `1px solid ${UI_COLOR_BORDER_DIVIDER_SOFT}`,
      my: 1.15
    }}
  />
)

const RefundApprovalProductCard = ({
  line,
  showLineActions,
  loading,
  onEditLine,
  onRejectLine
}) => {
  const originalQty = toSafeNumber(line.original_quantity ?? line.quantity)
  const verifiedQty = line.verified_quantity
  const lineTotal = originalQty * toSafeNumber(line.unit_price)
  const lineStatusUI = getRefundLineStatusUI(line)
  const isCounted = line.line_status !== 'pending'
  const hasLineUid =
    line.refund_item_uid !== undefined &&
    line.refund_item_uid !== null &&
    line.refund_item_uid !== ''
  const productCode = [line.product_db_ref, getPrimaryBarcode(line.product_barcode)]
    .filter(Boolean)
    .join(' - ')
  const packaging = line.price_unit || line.unit_of_sale || REFUND_APPROVAL_TEXT.defaultUnit

  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: `1px solid ${UI_COLOR_BORDER_CARD_NEUTRAL}`,
        backgroundColor: isCounted ? UI_COLOR_SURFACE_SUCCESS_ROW : UI_COLOR_WHITE,
        boxShadow: UI_COLOR_SHADOW_CARD,
        p: { xs: 1.75, sm: 2 },
        mb: 1.5
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'flex-start',
          gap: 1.5
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: 14, sm: 14 },
              fontWeight: 800,
              color: UI_COLOR_TEXT_CARD_BODY,
              textTransform: 'uppercase',
              lineHeight: 1.15,
              letterSpacing: 0,
              overflowWrap: 'anywhere'
            }}
          >
            {line.product_name || UI_TEXT_EM_DASH}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 13, sm: 13 },
              fontWeight: 500,
              color: UI_COLOR_TEXT_CODE,
              mt: 0.45,
              lineHeight: 1.25,
              letterSpacing: 0,
              overflowWrap: 'anywhere'
            }}
          >
            {productCode || UI_TEXT_EM_DASH}
          </Typography>
        </Box>

        {showLineActions && (
          <Box sx={{ flexShrink: 0 }}>
            {isCounted ? (
              <IconButton
                size="small"
                aria-label={`${REFUND_APPROVAL_TEXT.editCountAriaPrefix} ${line.product_name || line.product_db_ref || REFUND_APPROVAL_TEXT.productFallback}`}
                onClick={() => onEditLine(line.refund_item_uid)}
                disabled={loading || !hasLineUid}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '6px',
                  backgroundColor: UI_COLOR_PRIMARY_ALT,
                  color: UI_COLOR_WHITE,
                  '&:hover': { backgroundColor: UI_COLOR_PRIMARY_PRESS },
                  '&.Mui-disabled': {
                    backgroundColor: UI_COLOR_SURFACE_DISABLED_BTN,
                    color: UI_COLOR_WHITE
                  }
                }}
              >
                <GoogleIcon name="border_color" size={21} color={UI_COLOR_WHITE} />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                aria-label={`${REFUND_APPROVAL_TEXT.rejectAriaPrefix} ${line.product_name || line.product_db_ref || REFUND_APPROVAL_TEXT.productFallback}`}
                disabled={loading || !hasLineUid}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '6px',
                  border: `1px solid ${UI_COLOR_DANGER_OUTLINE}`,
                  color: UI_COLOR_DANGER_OUTLINE,
                  backgroundColor: UI_COLOR_WHITE,
                  '&:hover': { backgroundColor: UI_COLOR_DANGER_SURFACE },
                  '&.Mui-disabled': {
                    borderColor: UI_COLOR_BORDER_MEDIUM,
                    color: UI_COLOR_TEXT_DISABLED
                  }
                }}
                onClick={() => onRejectLine(line)}
              >
                <GoogleIcon name="close" size={28} color="inherit" />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <SectionDivider />

      <Stack spacing={0}>
        <DetailRow label={REFUND_APPROVAL_TEXT.cardPackaging} value={packaging} />
        <DetailRow label={REFUND_APPROVAL_TEXT.cardMotive} value={line.motive || UI_TEXT_EM_DASH} />
        <DetailRow
          label={REFUND_APPROVAL_TEXT.cardCondition}
          value={getRefundLineCondition(line)}
          labelSuffix=""
        />
      </Stack>

      <SectionDivider />

      <Stack spacing={0}>
        <DetailRow
          label={REFUND_APPROVAL_TEXT.cardOriginalQty}
          value={formatQuantity(originalQty)}
        />
        <DetailRow
          label={REFUND_APPROVAL_TEXT.cardVerifiedQty}
          value={verifiedQty != null ? formatQuantity(verifiedQty) : UI_TEXT_EM_DASH}
        />
        <DetailRow
          label={REFUND_APPROVAL_TEXT.cardUnitPrice}
          value={formatMoney(toSafeNumber(line.unit_price))}
        />
        <DetailRow label={REFUND_APPROVAL_TEXT.cardTotal} value={formatMoney(lineTotal)} />
      </Stack>

      <SectionDivider />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minWidth: 0
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: 14,
            fontWeight: 800,
            lineHeight: 1.25,
            letterSpacing: 0,
            flexShrink: 0
          }}
        >
          {REFUND_APPROVAL_TEXT.cardStatus}
        </Typography>
        <Box
          component="span"
          className={`order-status ${lineStatusUI.className}`}
          sx={{
            width: 100,
            maxWidth: '100%',
            height: 22,
            px: 1.5,
            borderRadius: '6px',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: 1,
            flexShrink: 1
          }}
        >
          {lineStatusUI.label}
        </Box>
      </Box>
    </Box>
  )
}

export default RefundApprovalProductCard
